import { __rest } from "tslib";
import { bool, func, node, object, oneOfType, shape, string } from 'prop-types';
import React, { useCallback, useMemo, useRef } from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useHandleSubmitWithDecoding, useInitialValuesWithEncoding } from './transformFieldNames';
import useRenderForm from './useRenderForm';
import './Form.less';
import { focusAndSmoothlyScrollIntoView } from '../../utils';
const NOOP = () => false;
export default function Form(props) {
    const { title, children, legend, renderButtons, renderAfterLabel, disabled = false, onSubmit = NOOP, onSuccess = NOOP, initialValues, showAllErrors = false, validateOnBlur = true, valueTransformer, id, fieldErrorMessage = 'The following fields need your attention:', mutators, subscription, className } = props, otherProps = __rest(props, ["title", "children", "legend", "renderButtons", "renderAfterLabel", "disabled", "onSubmit", "onSuccess", "initialValues", "showAllErrors", "validateOnBlur", "valueTransformer", "id", "fieldErrorMessage", "mutators", "subscription", "className"]);
    /**
     * Field names must be encoded (and transformed) before rendering
     * and decoded again before submission - see `transformFieldNames.js` for reasoning
     */
    const refForErrorAlert = useRef();
    const handleSubmitForm = useHandleSubmitWithDecoding(onSubmit, valueTransformer, initialValues);
    const actualInitialValues = useInitialValuesWithEncoding(initialValues, valueTransformer);
    const renderForm = useRenderForm({
        title,
        legend,
        children,
        renderButtons,
        renderAfterLabel,
        disabled,
        showAllErrors,
        id,
        fieldErrorMessage,
        className,
        subscription,
        onSuccess,
        refForErrorAlert,
    });
    const mutatorsToUse = useMemo(() => (Object.assign(Object.assign({}, mutators), arrayMutators)), [mutators]);
    const afterSubmitDecorator = useCallback((form) => {
        const originalSubmit = form.submit;
        const afterSubmit = () => {
            focusAndSmoothlyScrollIntoView(refForErrorAlert.current);
        };
        // overwrite submit function
        form.submit = () => {
            // handle async or sync submit variant
            const result = originalSubmit.call(form);
            // eslint-disable-next-line promise/prefer-await-to-then
            if (typeof (result === null || result === void 0 ? void 0 : result.then) === 'function') {
                // eslint-disable-next-line promise/prefer-await-to-then
                result.then(afterSubmit, NOOP);
            }
            else {
                afterSubmit();
            }
            return result;
        };
        // reset the submit function on cleanup
        return () => {
            form.submit = originalSubmit;
        };
    }, []);
    return (React.createElement(FinalForm, Object.assign({}, otherProps, { subscription: subscription, mutators: mutatorsToUse, initialValues: actualInitialValues, onSubmit: handleSubmitForm, render: renderForm, decorators: [afterSubmitDecorator], validateOnBlur: validateOnBlur })));
}
Form.propTypes = {
    /** HTML ID to apply to the underlying <form> element */
    id: string,
    /** If set, the form will display a title heading using the standard style */
    title: node,
    /** If set, the form will display a key or legend between the title and the actual form */
    legend: node,
    /** The fields that make up the body of the form */
    children: oneOfType([node, func]),
    /** Render function to display buttons bottom of the form. Takes the following arguments:
     * `disabled` - true if the whole form is disabled. Your buttons should obey this!
     * `formState` - as given by the `useFormState()` hook - see https://final-form.org/docs/final-form/types/FormState
     * `formApi` - as given by the `useForm()` hook - see https://final-form.org/docs/final-form/types/FormApi
     */
    renderButtons: func,
    /** Render function to create element that appears directly after field label (e.g. a "History" button) */
    renderAfterLabel: func,
    /** A callback triggered when the form is submitted (usually by the user activating a button with type `submit`). The form data is passed in. */
    onSubmit: func,
    /** A callback triggered after the form has been successfully submitted */
    onSuccess: func,
    /** If true, all fields and buttons will be non-interactive */
    disabled: bool,
    /** If true, then the form will immediately display all validation errors without waiting for the user to interact with a field */
    showAllErrors: bool,
    /** If true, then the form will only perform validation when a field loses focus. If set to false, the form will validate on every value change. */
    validateOnBlur: bool,
    /** The initial state of the form */
    initialValues: shape({}),
    /** This allows you to apply a transform to form values before they get displayed in the form, and reverse it just before submission */
    valueTransformer: shape({
        /** Takes an initial value and returns the value to be given to the form field */
        toFormValue: func,
        /** Takes a value from form field and returns the value to be submitted */
        toSubmittedValue: func,
    }),
    /** Name applied to the underlying form element (allows submit buttons to exist outside the <form> element by using the 'form' attribute) */
    name: string,
    /** Message used to warn of field errors after submission (in the error alert at the bottom of the form). */
    fieldErrorMessage: node,
    /** object of key-function pairs of final form mutators - see https://final-form.org/docs/final-form/types/Mutator */
    mutators: shape({}),
    /** CSS class applied to the underlying form element */
    className: string,
    /** See https://final-form.org/docs/react-final-form/types/FormProps#subscription */
    subscription: object,
};
//# sourceMappingURL=Form.js.map